/**
 * Turn a pipeline's published options schema into form fields.
 *
 * The API publishes each pipeline's ``options`` as JSON Schema (pydantic's
 * model_json_schema). Only top-level scalar properties become fields: that
 * is every knob a pipeline exposes today, and anything richer is a sign the
 * pipeline wants a form of its own rather than a generic one. A field left
 * blank is omitted from the submit so the pipeline's own default applies.
 */

import { isRecord } from '@/lib/guards'

export type OptionFieldKind = 'string' | 'number' | 'boolean'

export interface OptionField {
  name: string
  label: string
  kind: OptionFieldKind
  description: string
  /** The schema default, or null when the property has none. */
  defaultValue: string | number | boolean | null
  /** Allowed values when the schema enumerates them, else null. */
  choices: string[] | null
}

/** Form state: text inputs hold strings, checkboxes hold booleans. */
export type OptionValues = Record<string, string | boolean>

export type OptionsForSubmit = Record<string, string | number | boolean>

function kindOf(type: unknown): OptionFieldKind | null {
  if (type === 'string') return 'string'
  if (type === 'integer' || type === 'number') return 'number'
  if (type === 'boolean') return 'boolean'
  return null
}

function defaultOf(raw: unknown, kind: OptionFieldKind): string | number | boolean | null {
  if (kind === 'string' && typeof raw === 'string') return raw
  if (kind === 'number' && typeof raw === 'number') return raw
  if (kind === 'boolean' && typeof raw === 'boolean') return raw
  return null
}

function choicesOf(raw: unknown): string[] | null {
  if (!Array.isArray(raw)) return null
  const values = raw.filter((v): v is string => typeof v === 'string')
  return values.length === raw.length && values.length > 0 ? values : null
}

export function optionFieldsFromSchema(schema: Record<string, unknown>): OptionField[] {
  const properties = isRecord(schema.properties) ? schema.properties : {}
  const fields: OptionField[] = []
  for (const [name, raw] of Object.entries(properties)) {
    if (!isRecord(raw)) continue
    const kind = kindOf(raw.type)
    if (kind === null) continue
    fields.push({
      name,
      label: typeof raw.title === 'string' ? raw.title : name,
      kind,
      description: typeof raw.description === 'string' ? raw.description : '',
      defaultValue: defaultOf(raw.default, kind),
      choices: kind === 'string' ? choicesOf(raw.enum) : null,
    })
  }
  return fields
}

/** The form's starting values: the schema defaults, blank where there are none. */
export function initialOptionValues(fields: OptionField[]): OptionValues {
  const values: OptionValues = {}
  for (const field of fields) {
    if (field.kind === 'boolean') {
      values[field.name] = field.defaultValue === true
    } else {
      values[field.name] = field.defaultValue === null ? '' : String(field.defaultValue)
    }
  }
  return values
}

/** The options object to submit: typed by the schema, blanks left out. */
export function optionsForSubmit(fields: OptionField[], values: OptionValues): OptionsForSubmit {
  const options: OptionsForSubmit = {}
  for (const field of fields) {
    const value = values[field.name]
    if (field.kind === 'boolean') {
      options[field.name] = value === true
      continue
    }
    if (typeof value !== 'string' || value.trim() === '') continue
    if (field.kind === 'number') {
      const parsed = Number(value)
      if (Number.isNaN(parsed)) {
        throw new Error(`Option ${field.name} must be a number, got ${value}`)
      }
      options[field.name] = parsed
      continue
    }
    options[field.name] = value.trim()
  }
  return options
}
